import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import {
  Dropdown,
  Iconography,
  IconographySizes,
  Loader,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import {
  getValidationDocumentationSagaAction,
  getValidationHistoryDetailSagaAction,
  saveValidationDocumentationSagaAction,
} from "../../../../../../../../redux/saga/validation-documentation/saga-actions";
import { CategoryType } from "../../../../../../../../redux/slices/validation-documentation/Types";
import RequirementDescriptionModal from "../modals/RequirementDescriptionModal";
import HLScenariosModal from "../modals/HLScenariosModal";
import PartnerCommentsModal from "../modals/PartnerCommentsModal";
import HIDCommentsModal from "../modals/HIDCommentsModal";
import TestCaseDetailsModal from "../modals/TestCaseDetailsModal";
import "./DocumentationTable.scss";

interface DocumentationTableProps {
  isHIDUser: boolean;
  activeCategory: string;
  isHistoryView?: boolean;
  isEditable?: boolean;
  disableEditing?: boolean;
  technology?: string;
  integrationType?: string;
  integrationId?: number;
  onFormDataChange?: (
    formData: Record<
      string,
      {
        implementationStatus: string | null;
        verificationStatus: string | null;
        partnerComments: string;
        hidComments: string;
      }
    >
  ) => void;
  parentFormData?: Record<
    string,
    {
      implementationStatus: string | null;
      verificationStatus: string | null;
      partnerComments: string;
      hidComments: string;
    }
  >;
  recentHistoryDocumentation?: CategoryType[] | null;
  isNewVersion?: boolean;
  /** validation_submission: allow editing implementation status and partner comments. */
  canEditSubmission?: boolean;
  /** validation_approval: allow editing verification status and HID comments. */
  canEditApproval?: boolean;
}

interface ValidationRowData {
  id: string;
  requirementCode: string;
  requirementType: string;
  hlScenarios: string;
  description: string;
  testCases: string[];
  implementationStatus: string | null;
  verificationStatus: string | null;
  partnerComments: string;
  hidComments: string;
}

type SortColumn = "requirementCode" | "requirementType" | null;
type SortDirection = "asc" | "desc";

// ========== TEMPORARY: Data entry partner view emails. Remove with TEMPORARY block. ==========
const DATA_ENTRY_PARTNER_VIEW_EMAILS = [
  "nithin.george@hidglobal.com",
  "swetha.patnam@hidglobal.com",
  "chris.mclean@hidglobal.com",
  "andrew.vu@hidglobal.com"
];

const isDataEntryUserEmail = (email?: string | null): boolean =>
  !!email && DATA_ENTRY_PARTNER_VIEW_EMAILS.includes(email.toLowerCase());
// ========== TEMPORARY: end ==========

// Helper function to format test case ID with leading zeros
const formatTestCaseId = (testCaseId: string): string => {
  // Extract numeric part (handle formats like "1", "01", "TC-1", etc.)
  const numericId = testCaseId.replace(/^TC-?/i, "").trim();
  const num = parseInt(numericId, 10);
  if (isNaN(num)) return `TC-${testCaseId}`;
  // Format with leading zero for single digits (01-09)
  return `TC-${num.toString().padStart(2, "0")}`;
};

const DocumentationTable: React.FC<DocumentationTableProps> = ({
  isHIDUser,
  activeCategory,
  isHistoryView = false,
  isEditable = true,
  disableEditing = false,
  technology,
  integrationType,
  integrationId,
  onFormDataChange,
  parentFormData,
  recentHistoryDocumentation,
  isNewVersion = false,
  canEditSubmission = true,
  canEditApproval = true,
}) => {
  const dispatch = useAppDispatch();
  const { documentationData, historyDetailDocumentation, status } = useAppSelector(
    (state) => state.validation_documentation
  );
  const { status: projectStatus } = useAppSelector((state) => state.projects);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  // TEMPORARY: Treat this user as partner for Verification status (no dropdown). Remove with TEMPORARY block.
  const isDataEntryPartnerView = isDataEntryUserEmail(loggedUserDetail?.email);

  // Use appropriate data source based on view
  const currentDocumentationData = isHistoryView ? historyDetailDocumentation : documentationData;
  const currentLoading = isHistoryView ? status.historyDetailLoading : status.documentationLoading;
  const currentError = isHistoryView ? status.historyDetailError : status.documentationError;

  // Modal states
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [hlScenariosModalOpen, setHLScenariosModalOpen] = useState(false);
  const [partnerCommentsModalOpen, setPartnerCommentsModalOpen] =
    useState(false);
  const [hidCommentsModalOpen, setHIDCommentsModalOpen] = useState(false);
  const [testCaseModalOpen, setTestCaseModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ValidationRowData | null>(
    null
  );
  const [selectedTestCaseId, setSelectedTestCaseId] = useState<string>("");

  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Fetch documentation data when props change
  useEffect(() => {
    if (isHistoryView) {
      // History view: fetch using documentationId from parent component
      // This will be handled by ValidationHistoryDetail component
      // DocumentationTable doesn't need to fetch in history view
      return;
    }
    
    if (technology && integrationType && integrationId) {
      dispatch(
        getValidationDocumentationSagaAction(
          technology,
          integrationType,
          integrationId
        )
      );
    }
  }, [technology, integrationType, integrationId, isHistoryView, dispatch]);

  // Store requirement_id mapping for API calls
  const requirementIdMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (!currentDocumentationData || currentDocumentationData.length === 0) {
      return map;
    }

    const activeCategoryData = currentDocumentationData.find(
      (cat) => cat.category_name === activeCategory
    );

    if (activeCategoryData && activeCategoryData.requirements) {
      activeCategoryData.requirements.forEach((req) => {
        map[`req-${req.requirement_id}`] = req.requirement_id;
      });
    }

    return map;
  }, [currentDocumentationData, activeCategory]);

  // Get active category data for category_id
  const activeCategoryData = useMemo(() => {
    if (!currentDocumentationData || currentDocumentationData.length === 0) {
      return null;
    }
    return currentDocumentationData.find(
      (cat) => cat.category_name === activeCategory
    );
  }, [currentDocumentationData, activeCategory]);

  // Transform API data to table format
  const tableData: ValidationRowData[] = useMemo(() => {
    if (!activeCategoryData || !activeCategoryData.requirements) {
      return [];
    }

    // Map requirements to table rows
    return activeCategoryData.requirements.map((req) => {
      // Determine platform from integration_type (string or "Unknown")
      const requirementCode =
        req.requirement_code && req.requirement_code.trim() !== ""
          ? req.requirement_code
          : "Unknown";

      // Get all test cases as array
      const testCasesArray =
        req.test_cases &&
        Array.isArray(req.test_cases) &&
        req.test_cases.length > 0
          ? req.test_cases
          : [];

      return {
        id: `req-${req.requirement_id}`,
        requirementCode: requirementCode,
        requirementType: req.requirement_type,
        hlScenarios: req.high_level_scenario || "",
        description: req.description || "",
        testCases: testCasesArray,
        implementationStatus: req.implementation_status || null,
        verificationStatus: req.verification_status === "Completed" ? "Pass" : (req.verification_status || null),
        partnerComments: req.partner_comments || "",
        hidComments: req.hid_comments || "",
      };
    });
  }, [activeCategoryData]);

  // Form data for editable fields - initialize from API data and parent form data
  const [formData, setFormData] = useState<
    Record<
      string,
      {
        implementationStatus: string | null;
        verificationStatus: string | null;
        partnerComments: string;
        hidComments: string;
      }
    >
  >({});

  // Track which fields have been manually edited by the user
  const userEditsRef = useRef<Record<string, Set<string>>>({});
  
  // Track the last synced formData to prevent infinite loops
  const lastSyncedFormDataRef = useRef<typeof formData>({});
  
  // Track if we're currently syncing to prevent re-initialization from our own updates
  const isSyncingRef = useRef(false);
  
  // Track previous tableData to detect actual changes
  const prevTableDataRef = useRef<ValidationRowData[]>([]);

  // Initialize formData when tableData changes (category switch or API refetch)
  // Use parentFormData as initial source but only when tableData actually changes
  useEffect(() => {
    // Check if tableData actually changed
    const tableDataChanged = 
      prevTableDataRef.current.length !== tableData.length ||
      tableData.some((row, idx) => {
        const prevRow = prevTableDataRef.current[idx];
        return !prevRow || prevRow.id !== row.id;
      });
    
    if (!tableDataChanged && prevTableDataRef.current.length > 0) {
      return; // No change, skip update
    }
    
    prevTableDataRef.current = tableData;
    
    // Skip if we're currently syncing (to prevent loop)
    if (isSyncingRef.current) {
      return;
    }
    
    setFormData((prev) => {
      // Start with parent form data (contains all categories) merged with previous local state
      // This ensures we preserve data from all categories, not just the current one
      const updated = { ...(parentFormData || {}), ...prev };
      
      // Now update rows for the current category based on latest API data
      tableData.forEach((row) => {
        // Check if we have parent form data for this row (from any category)
        const parentRowData = parentFormData?.[row.id];
        const prevRowData = prev[row.id];
        
        // For status fields (implementationStatus, verificationStatus), preserve user selections
        // Priority: parentFormData > prev (local edits) > API data
        const userEdits = userEditsRef.current[row.id] || new Set();
        
        // Check if implementationStatus was user-edited OR if it has a value that differs from API
        const shouldPreserveImplementationStatus = 
          userEdits.has('implementationStatus') || 
          (prevRowData?.implementationStatus !== undefined && 
           prevRowData?.implementationStatus !== null &&
           prevRowData?.implementationStatus !== row.implementationStatus) ||
          (parentRowData?.implementationStatus !== undefined &&
           parentRowData?.implementationStatus !== null &&
           parentRowData?.implementationStatus !== row.implementationStatus);
        
        // Check if verificationStatus was user-edited OR if it has a value that differs from API
        const shouldPreserveVerificationStatus = 
          userEdits.has('verificationStatus') || 
          (prevRowData?.verificationStatus !== undefined && 
           prevRowData?.verificationStatus !== null &&
           prevRowData?.verificationStatus !== row.verificationStatus) ||
          (parentRowData?.verificationStatus !== undefined &&
           parentRowData?.verificationStatus !== null &&
           parentRowData?.verificationStatus !== row.verificationStatus);
        
        // Determine which value to use (priority: parent > prev > API)
        const implementationStatus = shouldPreserveImplementationStatus
          ? (parentRowData?.implementationStatus ?? prevRowData?.implementationStatus ?? row.implementationStatus)
          : row.implementationStatus;
        
        const verificationStatus = shouldPreserveVerificationStatus
          ? (parentRowData?.verificationStatus ?? prevRowData?.verificationStatus ?? row.verificationStatus)
          : row.verificationStatus;
        
        // Update this row, preserving status fields if they were user-edited
        updated[row.id] = {
          implementationStatus,
          verificationStatus,
          // Comments come from API (they may have been updated via save)
          partnerComments: row.partnerComments !== undefined ? row.partnerComments : (parentRowData?.partnerComments ?? prevRowData?.partnerComments ?? ""),
          hidComments: row.hidComments !== undefined ? row.hidComments : (parentRowData?.hidComments ?? prevRowData?.hidComments ?? ""),
        };
      });
      
      return updated;
    });
  }, [tableData]); // Only depend on tableData to prevent loops - parentFormData is used but not as dependency

  // Pre-fill implementation/verification status from recentHistoryDocumentation
  // This runs when recentHistoryDocumentation is loaded (for new version scenario OR when returning after admin processing)
  // Uses history values when documentation API shows "Not implemented" but history has submitted values
  useEffect(() => {
    if (!recentHistoryDocumentation || recentHistoryDocumentation.length === 0) return;
    if (tableData.length === 0) return;

    // Build a map of requirement_id -> requirement from recentHistoryDocumentation
    const recentHistoryMap = new Map<number, { implementation_status: string; verification_status: string }>();
    recentHistoryDocumentation.forEach((category) => {
      category.requirements?.forEach((req) => {
        recentHistoryMap.set(req.requirement_id, {
          implementation_status: req.implementation_status || "",
          verification_status: req.verification_status || "",
        });
      });
    });

    setFormData((prev) => {
      const updated = { ...prev };
      let hasChanges = false;

      tableData.forEach((row) => {
        const requirementId = parseInt(row.id.replace("req-", ""), 10);
        const recentHistoryReq = recentHistoryMap.get(requirementId);
        const rowId = row.id;

        // Ensure row exists in formData
        if (!updated[rowId]) {
          updated[rowId] = {
            implementationStatus: null,
            verificationStatus: null,
            partnerComments: "",
            hidComments: "",
          };
        }

        const currentRow = updated[rowId];
        const userEdits = userEditsRef.current[rowId] || new Set();
        
        // Use history values when:
        // 1. Field hasn't been user-edited
        // 2. AND (field is empty/null OR documentation API shows "Not implemented" but history shows different value)
        // This handles both new version scenario and returning after admin processing
        if (recentHistoryReq) {
          const docImplementationStatus = row.implementationStatus;
          const docVerificationStatus = row.verificationStatus;
          
          const historyImplementationStatus = recentHistoryReq.implementation_status || null;
          const historyVerificationStatus = recentHistoryReq.verification_status === "Completed" ? "Pass" : (recentHistoryReq.verification_status || null);
          
          // Check if we should use history value for implementation status
          // Use history if: not user-edited AND (
          //   current is empty/null OR 
          //   (doc shows "Not implemented" and history shows a different non-empty value)
          // )
          const isCurrentEmptyOrNull = !currentRow.implementationStatus || currentRow.implementationStatus === null;
          const docShowsNotImplemented = docImplementationStatus === "Not implemented";
          const historyHasDifferentValue = historyImplementationStatus && 
            historyImplementationStatus !== "Not implemented" && 
            historyImplementationStatus !== "";
          const shouldUseHistoryImplementation = 
            !userEdits.has('implementationStatus') &&
            (isCurrentEmptyOrNull || (docShowsNotImplemented && historyHasDifferentValue));
          
          // Check if we should use history value for verification status
          // Use history if: not user-edited AND (
          //   current is empty/null OR 
          //   (doc shows "Pending" and history shows a different non-empty value)
          // )
          const isVerificationEmptyOrNull = !currentRow.verificationStatus || currentRow.verificationStatus === null;
          const docShowsPending = docVerificationStatus === "Pending";
          const historyHasDifferentVerification = historyVerificationStatus && 
            historyVerificationStatus !== "Pending" && 
            historyVerificationStatus !== "";
          const shouldUseHistoryVerification = 
            !userEdits.has('verificationStatus') &&
            (isVerificationEmptyOrNull || (docShowsPending && historyHasDifferentVerification));

          // Only update if we should use history values and they exist
          if ((shouldUseHistoryImplementation && historyImplementationStatus) || 
              (shouldUseHistoryVerification && historyVerificationStatus)) {
            updated[rowId] = {
              ...updated[rowId],
              implementationStatus: shouldUseHistoryImplementation && historyImplementationStatus 
                ? historyImplementationStatus 
                : currentRow.implementationStatus,
              verificationStatus: shouldUseHistoryVerification && historyVerificationStatus 
                ? historyVerificationStatus 
                : currentRow.verificationStatus,
            };
            hasChanges = true;
          }
        }
      });

      return hasChanges ? updated : prev;
    });
  }, [recentHistoryDocumentation, tableData]);

  // Sync form data changes to parent component
  // Only sync when formData actually changes (not just on re-render)
  useEffect(() => {
    // Skip if we're currently syncing (to prevent loop)
    if (isSyncingRef.current) {
      return;
    }
    
    // Simple comparison: check if formData reference changed or if keys/values differ
    const lastSynced = lastSyncedFormDataRef.current;
    let hasChanged = false;
    
    // Check if keys differ
    const currentKeys = Object.keys(formData).sort();
    const lastKeys = Object.keys(lastSynced).sort();
    if (currentKeys.length !== lastKeys.length || 
        currentKeys.some((key, idx) => key !== lastKeys[idx])) {
      hasChanged = true;
    } else {
      // Check if values differ for any key
      for (const key of currentKeys) {
        const currentRow = formData[key];
        const lastRow = lastSynced[key];
        if (!lastRow ||
            currentRow.implementationStatus !== lastRow.implementationStatus ||
            currentRow.verificationStatus !== lastRow.verificationStatus ||
            currentRow.partnerComments !== lastRow.partnerComments ||
            currentRow.hidComments !== lastRow.hidComments) {
          hasChanged = true;
          break;
        }
      }
    }
    
    if (hasChanged && onFormDataChange) {
      isSyncingRef.current = true;
      // Deep copy to store in ref
      const formDataCopy: typeof formData = {};
      Object.keys(formData).forEach((key) => {
        formDataCopy[key] = { ...formData[key] };
      });
      lastSyncedFormDataRef.current = formDataCopy;
      onFormDataChange(formData);
      // Reset sync flag after a brief delay to allow parent to update
      setTimeout(() => {
        isSyncingRef.current = false;
      }, 0);
    }
  }, [formData, onFormDataChange]);

  const implementationStatusOptions = [
    { label: "Select", value: "" },
    { label: "Implemented", value: "Implemented" },
    { label: "Not implemented", value: "Not implemented" },
    { label: "Not applicable", value: "Not applicable" },
  ];

  const verificationStatusOptions = [
    { label: "Select", value: "" },
    { label: "Pass", value: "Pass" },
    { label: "Failed", value: "Failed" },
    { label: "Not Applicable", value: "Not Applicable" },
  ];

  const handleImplementationStatusChange = useCallback(
    (rowId: string, value: any) => {
      // Mark this field as user-edited
      if (!userEditsRef.current[rowId]) {
        userEditsRef.current[rowId] = new Set();
      }
      userEditsRef.current[rowId].add('implementationStatus');
      
      setFormData((prev) => {
        const existingRow = prev[rowId] || {
          implementationStatus: null as string | null,
          verificationStatus: null as string | null,
          partnerComments: "",
          hidComments: "",
        };
        const newValue: string | null = value?.value || null;
        return {
          ...prev,
          [rowId]: {
            ...existingRow,
            implementationStatus: newValue,
          },
        };
      });
    },
    []
  );

  const handleVerificationStatusChange = useCallback(
    (rowId: string, value: any) => {
      // Mark this field as user-edited
      if (!userEditsRef.current[rowId]) {
        userEditsRef.current[rowId] = new Set();
      }
      userEditsRef.current[rowId].add('verificationStatus');
      
      setFormData((prev) => {
        const existingRow = prev[rowId] || {
          implementationStatus: null as string | null,
          verificationStatus: null as string | null,
          partnerComments: "",
          hidComments: "",
        };
        const newValue: string | null = value?.value || null;
        return {
          ...prev,
          [rowId]: {
            ...existingRow,
            verificationStatus: newValue,
          },
        };
      });
    },
    []
  );

  const handleOpenDescription = (row: ValidationRowData) => {
    setSelectedRow(row);
    setDescriptionModalOpen(true);
  };

  const handleOpenHLScenarios = (row: ValidationRowData) => {
    setSelectedRow(row);
    setHLScenariosModalOpen(true);
  };

  const handleOpenTestCase = (row: ValidationRowData, testCaseId: string) => {
    setSelectedRow(row);
    setSelectedTestCaseId(testCaseId);
    setTestCaseModalOpen(true);
    // The modal's useEffect will handle fetching the test case data
  };

  const handleOpenPartnerComments = (row: ValidationRowData) => {
    setSelectedRow(row);
    setPartnerCommentsModalOpen(true);
  };

  const handleOpenHIDComments = (row: ValidationRowData) => {
    setSelectedRow(row);
    setHIDCommentsModalOpen(true);
  };

  const handleSavePartnerComments = async (comments: string) => {
    if (
      selectedRow &&
      technology &&
      integrationType &&
      integrationId &&
      activeCategoryData &&
      currentDocumentationData
    ) {
      // Extract requirement_id from row id (format: req-{requirement_id})
      const requirementId = requirementIdMap[selectedRow.id];

      // Update local state - preserve all existing fields
      // Also capture current formData values to include in save
      let currentFormDataRow: {
        implementationStatus: string | null;
        verificationStatus: string | null;
        partnerComments: string;
        hidComments: string;
      } | undefined;
      
      setFormData((prev) => {
        const existingRow = prev[selectedRow.id] || {
          implementationStatus: null as string | null,
          verificationStatus: null as string | null,
          partnerComments: "",
          hidComments: "",
        };
        currentFormDataRow = {
          ...existingRow,
          partnerComments: comments,
        };
        return {
          ...prev,
          [selectedRow.id]: currentFormDataRow,
        };
      });

      if (requirementId && loggedUserDetail?.ID && activeCategoryData && currentFormDataRow) {
        // Build categories array with only the changed category and requirement
        // Include implementation_status and verification_status ONLY if they were user-edited
        // This ensures user selections are preserved after refetch without saving unintended changes
        const requirementToSave: any = {
          requirement_id: requirementId,
          partner_comments: comments,
        };
        
        // Only save status fields if they were explicitly edited by the user
        const userEdits = userEditsRef.current[selectedRow.id] || new Set();
        if (userEdits.has('implementationStatus') && currentFormDataRow.implementationStatus) {
          requirementToSave.implementation_status = currentFormDataRow.implementationStatus;
        }
        if (userEdits.has('verificationStatus') && currentFormDataRow.verificationStatus) {
          requirementToSave.verification_status = currentFormDataRow.verificationStatus;
        }
        
        const categoriesToSave = [
          {
            category_id: activeCategoryData.category_id,
            requirements: [requirementToSave],
          },
        ];

        // Call API with only the changed category and requirement
        dispatch(
          saveValidationDocumentationSagaAction({
            technology,
            integration_type: integrationType,
            integration_id: integrationId,
            user: loggedUserDetail.ID,
            categories: categoriesToSave,
          })
        );
      }
    }
    setPartnerCommentsModalOpen(false);
  };

  const handleSaveHIDComments = async (comments: string) => {
    if (
      selectedRow &&
      technology &&
      integrationType &&
      integrationId &&
      activeCategoryData &&
      currentDocumentationData
    ) {
      // Extract requirement_id from row id (format: req-{requirement_id})
      const requirementId = requirementIdMap[selectedRow.id];

      // Update local state - preserve all existing fields
      // Also capture current formData values to include in save
      let currentFormDataRow: {
        implementationStatus: string | null;
        verificationStatus: string | null;
        partnerComments: string;
        hidComments: string;
      } | undefined;
      
      setFormData((prev) => {
        const existingRow = prev[selectedRow.id] || {
          implementationStatus: null as string | null,
          verificationStatus: null as string | null,
          partnerComments: "",
          hidComments: "",
        };
        currentFormDataRow = {
          ...existingRow,
          hidComments: comments,
        };
        return {
          ...prev,
          [selectedRow.id]: currentFormDataRow,
        };
      });

      if (requirementId && loggedUserDetail?.ID && activeCategoryData && currentFormDataRow) {
        // Build categories array with only the changed category and requirement
        // Include implementation_status and verification_status ONLY if they were user-edited
        // This ensures user selections are preserved after refetch without saving unintended changes
        const requirementToSave: any = {
          requirement_id: requirementId,
          hid_comments: comments,
        };
        
        // Only save status fields if they were explicitly edited by the user
        const userEdits = userEditsRef.current[selectedRow.id] || new Set();
        if (userEdits.has('implementationStatus') && currentFormDataRow.implementationStatus) {
          requirementToSave.implementation_status = currentFormDataRow.implementationStatus;
        }
        if (userEdits.has('verificationStatus') && currentFormDataRow.verificationStatus) {
          requirementToSave.verification_status = currentFormDataRow.verificationStatus;
        }
        
        const categoriesToSave = [
          {
            category_id: activeCategoryData.category_id,
            requirements: [requirementToSave],
          },
        ];

        // Call API with only the changed category and requirement
        dispatch(
          saveValidationDocumentationSagaAction({
            technology,
            integration_type: integrationType,
            integration_id: integrationId,
            user: loggedUserDetail.ID,
            categories: categoriesToSave,
          })
        );
      }
    }
    setHIDCommentsModalOpen(false);
  };

  const getImplementationStatusChip = (status: string | null) => {
    if (!status) return null;
    let className = "";
    
    if (status === "Implemented") {
      className = "implemented";
    } else if (status === "Not implemented") {
      className = "not-implemented";
    } else if (status === "Not applicable") {
      className = "not-applicable";
    }
    
    return (
      <Chip
        label={status}
        className={`status-chip ${className}`}
      />
    );
  };

  const getVerificationStatusChip = (status: string | null) => {
    if (!status) return null;
    let className = "";

    if (status === "Pass" || status === "Completed") {
      className = "completed";
    } else if (status === "Failed") {
      className = "failed";
    } else if (status === "Not Applicable") {
      className = "not-applicable";
    }

    return <Chip label={status === "Completed" ? "Pass" : status} className={`status-chip ${className}`} />;
  };

  // Helper function to render implementation status cell
  const renderImplementationStatus = (rowId: string) => {
    const status = formData[rowId]?.implementationStatus;
    const selectedOption = status
      ? implementationStatusOptions.find((opt) => opt.value === status) || null
      : null;

    const shouldShowDropdown = isHistoryView
      ? isHIDUser && isEditable && canEditApproval
      : !disableEditing && canEditSubmission;

    if (shouldShowDropdown) {
      return (
        <Dropdown
          id={`implementation-${rowId}`}
          label=""
          placeholder="Select"
          options={implementationStatusOptions}
          selected={selectedOption}
          onSelect={(value) => handleImplementationStatusChange(rowId, value)}
          menuPortalTarget={document.body}
        />
      );
    }

    return getImplementationStatusChip(status);
  };

  // Helper function to render verification status cell
  const renderVerificationStatus = (rowId: string) => {
    const status = formData[rowId]?.verificationStatus;
    const selectedOption = status
      ? verificationStatusOptions.find((opt) => opt.value === status) || null
      : null;

    // TEMPORARY: For data-entry user (kiran): on main validation tab show chip; on history page show dropdown. Remove with TEMPORARY block.
    const effectiveIsHIDUserForVerification = isHistoryView
      ? isHIDUser || isDataEntryPartnerView // History page: show dropdown for HID and for data-entry user (kiran)
      : isHIDUser && !isDataEntryPartnerView; // Main tab: show dropdown only for HID, not kiran
    const shouldShowDropdown = isHistoryView
      ? effectiveIsHIDUserForVerification && isEditable && canEditApproval
      : effectiveIsHIDUserForVerification && !disableEditing && canEditApproval;

    if (shouldShowDropdown) {
      return (
        <Dropdown
          id={`verification-${rowId}`}
          label=""
          placeholder="Select"
          options={verificationStatusOptions}
          selected={selectedOption}
          onSelect={(value) => handleVerificationStatusChange(rowId, value)}
          menuPortalTarget={document.body}
        />
      );
    }

    return getVerificationStatusChip(status);
  };

  const getCommentsLabel = (): string => {
    return "View";
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New column, start with ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <Iconography icon="sort" size={IconographySizes.Small} />;
    }
    return sortDirection === "asc" ? (
      <Iconography icon="chevronUp" size={IconographySizes.Small} />
    ) : (
      <Iconography icon="chevronDown" size={IconographySizes.Small} />
    );
  };

  const sortedTableData = useMemo(() => {
    if (!sortColumn) return tableData;

    return [...tableData].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [tableData, sortColumn, sortDirection]);

  // Show loading or error states
  if (currentLoading || projectStatus.integrationDetailLoading) {
    return (
      <div className="documentation-table-wrapper">
        <div className="loading-documentation-data">
          <Loader />
          Loading documentation data...
        </div>
      </div>
    );
  }

  if (currentError) {
    return (
      <div className="documentation-table-wrapper">
        <div className="error-documentation-data">
          Error: {currentError}
        </div>
      </div>
    );
  }

  return (
    <div className="documentation-table-wrapper">
      <TableContainer component={Paper} className="documentation-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <div
                  className={`sortable-header ${
                    sortColumn === "requirementCode" ? "active" : ""
                  }`}
                  onClick={() => handleSort("requirementCode")}
                >
                  Requirement ID
                  {getSortIcon("requirementCode")}
 
                </div>
              </TableCell>
              <TableCell>
                <div
                  className={`sortable-header ${
                    sortColumn === "requirementType" ? "active" : ""
                  }`}
                  onClick={() => handleSort("requirementType")}
                >
                  Requirement Type
                  {getSortIcon("requirementType")}
                </div>
              </TableCell>
              <TableCell>H/L Scenarios</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Test Case</TableCell>
              <TableCell>Implementation Status</TableCell>
              <TableCell>Verification Status</TableCell>
              <TableCell>Partner Comments</TableCell>
              <TableCell>HID Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTableData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No requirements found for this category.
                </TableCell>
              </TableRow>
            ) : (
              sortedTableData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.requirementCode}</TableCell>
                  <TableCell>{row.requirementType}</TableCell>
                  <TableCell>
                   {row.hlScenarios}
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      className="view-link"
                      onClick={() => handleOpenDescription(row)}
                    >
                      View
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="array-column">
                      {row.testCases && row.testCases.length > 0 ? (
                        row.testCases.map((testCaseId: string) => (
                          <button
                            key={testCaseId}
                            type="button"
                            className="test-case-link"
                            onClick={() => handleOpenTestCase(row, testCaseId)}
                          >
                            {formatTestCaseId(testCaseId)}
                          </button>
                        ))
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {renderImplementationStatus(row.id)}
                  </TableCell>
                  <TableCell>
                    {renderVerificationStatus(row.id)}
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      className="view-link"
                      onClick={() => handleOpenPartnerComments(row)}
                    >
                      {getCommentsLabel()}
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      className="view-link"
                      onClick={() => handleOpenHIDComments(row)}
                    >
                      {getCommentsLabel()}
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {descriptionModalOpen && selectedRow && (
        <RequirementDescriptionModal
          description={selectedRow.description}
          onClose={() => setDescriptionModalOpen(false)}
        />
      )}

      {hlScenariosModalOpen && selectedRow && (
        <HLScenariosModal
          scenarios={selectedRow.hlScenarios}
          onClose={() => setHLScenariosModalOpen(false)}
        />
      )}

      {testCaseModalOpen && (
        <TestCaseDetailsModal
          testCaseId={selectedTestCaseId}
          onClose={() => {
            setTestCaseModalOpen(false);
            setSelectedTestCaseId("");
          }}
        />
      )}

      {partnerCommentsModalOpen && selectedRow && (
        <PartnerCommentsModal
          comments={formData[selectedRow.id]?.partnerComments || ""}
          onSave={handleSavePartnerComments}
          onClose={() => setPartnerCommentsModalOpen(false)}
          // TEMPORARY: allow data-entry emails (kiran, swetha) to edit partner comments.
          isReadOnly={
            (isHIDUser && !isDataEntryPartnerView) ||
            disableEditing ||
            !canEditSubmission
          }
        />
      )}

      {hidCommentsModalOpen && selectedRow && (
        <HIDCommentsModal
          comments={formData[selectedRow.id]?.hidComments || ""}
          onSave={handleSaveHIDComments}
          onClose={() => setHIDCommentsModalOpen(false)}
          isReadOnly={
            isHistoryView
              ? !isEditable || !canEditApproval
              : !isHIDUser || disableEditing || !canEditApproval
          }
        />
      )}
    </div>
  );
};

export default DocumentationTable;
